import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { AppButton, AppInput, SelectionDropdown } from "@libs/zapi-ui/custom";
import { AppLoader } from "@/zapi-admin/shared/components/AppLoader";
import { ProjectSelect } from "@/zapi-admin/shared/components/ProjectSelect/ui/ProjectSelect";
import { UserSelect } from "@/zapi-admin/pages/AiLogsPage/ui/UserSelect";
import { fetchClaudeRunFilters, fetchClaudeRuns } from "../model/api";
import { EMPTY_QUERY, RUN_KIND_LABEL, type TClaudeRun, type TClaudeRunsQuery } from "../model/types";
import { ClaudeRunsTable } from "./ClaudeRunsTable";

const PAGE_SIZE = 20;
const LIVE_REFRESH_MS = 10000;
const QUERY_KEYS = Object.keys(EMPTY_QUERY) as (keyof TClaudeRunsQuery)[];

const KIND_OPTIONS = [
  { label: "All kinds", value: "" },
  { label: RUN_KIND_LABEL.projectGeneration, value: "projectGeneration" },
  { label: RUN_KIND_LABEL.chat, value: "chat" },
  { label: RUN_KIND_LABEL.presentation, value: "presentation" }
];

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Running", value: "running" },
  { label: "Succeeded", value: "succeeded" },
  { label: "Failed", value: "failed" },
  { label: "Cancelled", value: "cancelled" }
];

const isActive = (run: TClaudeRun) =>
  run.status === "pending" || run.status === "running" || run.logStatus === "collecting";

export function ClaudeRunsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const query = QUERY_KEYS.reduce((acc, key) => ({ ...acc, [key]: searchParams.get(key) || "" }), {
    ...EMPTY_QUERY
  } as TClaudeRunsQuery);
  const [live, setLive] = useState(false);

  const updateParams = useCallback(
    (changes: Partial<TClaudeRunsQuery> & { page?: number }) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value === "" || value === undefined || (key === "page" && value === 1)) next.delete(key);
        else next.set(key, String(value));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const setFilter = (key: keyof TClaudeRunsQuery) => (value: string) => updateParams({ [key]: value, page: 1 });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["claude-runs", query, page],
    queryFn: () => fetchClaudeRuns(query, page, PAGE_SIZE),
    refetchInterval: live ? LIVE_REFRESH_MS : false
  });
  const { data: filters } = useQuery({ queryKey: ["claude-runs-filters"], queryFn: fetchClaudeRunFilters });

  useEffect(() => {
    setLive(Boolean(data?.runs.some(isActive)));
  }, [data]);

  const organizationOptions = [
    { label: "All organizations", value: "" },
    ...(filters?.organizations.map((org) => ({ label: `${org.id}: ${org.name}`, value: String(org.id) })) ?? [])
  ];

  return (
    <div data-testid="claude-runs-view">
      <div className="flex flex-wrap gap-4 mb-6 items-end" data-testid="claude-runs-filters">
        <div className="w-[258px]">
          <SelectionDropdown
            placeholder="Organization"
            options={organizationOptions}
            value={organizationOptions.find((o) => o.value === query.organizationId)?.value ?? ""}
            onChange={setFilter("organizationId")}
            className="rounded-[8px]"
          />
        </div>
        <UserSelect userId={query.userId} setUserId={setFilter("userId")} />
        <ProjectSelect projectId={query.projectId} setProjectId={setFilter("projectId")} />
        <div className="w-[180px]">
          <SelectionDropdown
            placeholder="Kind"
            options={KIND_OPTIONS}
            value={query.kind}
            onChange={setFilter("kind")}
            className="rounded-[8px]"
          />
        </div>
        <div className="w-[180px]">
          <SelectionDropdown
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={query.status}
            onChange={setFilter("status")}
            className="rounded-[8px]"
          />
        </div>
        <div className="flex items-center gap-2">
          <AppInput
            type="date"
            value={query.from}
            onChange={(e) => setFilter("from")(e.target.value)}
            className="rounded-[8px] bg-innoveraZinc-50 w-[150px]"
            data-testid="claude-runs-from"
          />
          <span className="text-zinc-500 text-[14px]">to</span>
          <AppInput
            type="date"
            value={query.to}
            onChange={(e) => setFilter("to")(e.target.value)}
            className="rounded-[8px] bg-innoveraZinc-50 w-[150px]"
            data-testid="claude-runs-to"
          />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {Object.values(query).some(Boolean) && (
            <AppButton
              appearance="text"
              color="zinc950"
              size="sm"
              onClick={() => updateParams({ ...EMPTY_QUERY, page: 1 })}
            >
              Clear filters
            </AppButton>
          )}
          <AppButton
            appearance="outlined"
            color="zinc950"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 h-fit"
            data-testid="claude-runs-refresh"
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
            Refresh
          </AppButton>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <AppLoader />
        </div>
      )}
      {!isLoading && data && (
        <ClaudeRunsTable
          runs={data.runs}
          total={data.total}
          page={page}
          pageSize={PAGE_SIZE}
          setPage={(next) => updateParams({ page: next })}
        />
      )}
    </div>
  );
}
